import React, { useMemo } from 'react';
import type { Test } from '@mariana/labos-client';
import { WorksheetViewer } from '../WorksheetViewer';
import { SpreadsheetTable } from '../SpreadsheetTable';
import { formatAnalyticalValue } from '../../utils/formatValue';
import './ResultViewers.css';

interface ShimadzuResultViewerProps {
  test: Test;
}

const SHIMADZU_PARAMS: { key: string; label: string }[] = [
  { key: 'report_export_result_toc_', label: 'TOC' },
  { key: 'report_export_result_tc_', label: 'TC' },
  { key: 'report_export_result_ic_', label: 'IC' },
  { key: 'report_export_result_poc_', label: 'POC' },
  { key: 'report_export_result_npoc_', label: 'NPOC' },
  { key: 'report_export_result_tn_', label: 'TN' },
];

export const ShimadzuResultViewer: React.FC<ShimadzuResultViewerProps> = ({ test }) => {
  const worksheetData = test.attributes?.worksheet_data as Record<string, any> | undefined;
  const rawWorksheetData = test.attributes?.raw_worksheet_data as Record<string, Record<string, any>> | undefined;

  const reportedRows = useMemo(() => {
    if (!worksheetData) return [];
    return SHIMADZU_PARAMS.map(({ key, label }) => {
      const entry = worksheetData[key];
      const value = entry?.value ?? entry;
      return [label, formatAnalyticalValue(value)] as [string, string];
    });
  }, [worksheetData]);

  return (
    <div className="rv-container">
      {rawWorksheetData && (
        <div className="rv-section rv-section-raw">
          <h3 className="rv-section-title">Raw Worksheet</h3>
          <WorksheetViewer rawWorksheetData={rawWorksheetData} />
        </div>
      )}

      {worksheetData && reportedRows.length > 0 && (
        <div className="rv-section rv-section-reported">
          <h3 className="rv-section-title">Reported Values</h3>
          <SpreadsheetTable
            headers={['Parameter', 'Value']}
            rows={reportedRows}
            showRowNumbers={false}
          />
        </div>
      )}

      {!worksheetData && !rawWorksheetData && (
        <div className="rv-empty">No Shimadzu results available for this test.</div>
      )}
    </div>
  );
};
